import { gql } from "@apollo/client";

/**
 * PD (permanent disconnection) revival board.
 *
 * A SIM whose Sensorise cardState is "Terminated" has had BOTH of its
 * dual-profile subscriptions permanently disconnected. It cannot be recharged —
 * Sensorise accepts a Top-Up against one, charges for it, and moves nothing —
 * so reviving it is a four-step sequence spanning more than a day:
 *
 *   1. OTC RSU   automatic, fires when the case opens        ~1 hr
 *   2. IMSI      automatic, off the 3-hourly sweep            24 hr
 *   3. RSU       THIS BOARD'S BUTTON                        varies
 *   4. BPC       this board's second button                  ~2 hr
 *      The board can explicitly bypass the unsent RSU step after OTC RSU and
 *      IMSI have completed.
 *
 * Steps 1 and 2 run themselves. Steps 3 and 4 are buttons because each needs a
 * person: the RSU lands only when the device next sees a usable network, so
 * firing it while the vehicle is in a shed burns the attempt, and the operator
 * is the one who knows whether the customer has been told to switch the
 * ignition on.
 *
 * Backend: oem_tool_backend services/sim/pdCase.ts and sql/pd_case.sql.
 * Every date field is epoch SECONDS.
 */

export type PdCaseStatus =
  | "OPEN"
  | "IN_PROGRESS"
  | "AWAITING_RSU"
  | "AWAITING_BPC"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"
  // Closed for good by a person, usually a FAILED case. Never reopened.
  | "CLOSED";

export type PdStepStatus =
  | "PENDING"
  | "SENT"
  | "DONE"
  | "FAILED"
  | "BLOCKED"
  | "SKIPPED";

export interface PdCaseStep {
  stepId: string;
  stepKey: "OTC_RSU" | "IMSI" | "RSU" | "BPC";
  seq: number;
  label: string;
  status: PdStepStatus;
  srId: string | null;
  tokenNo: string | null;
  remoteStatus: string | null;
  remoteReason: string | null;
  sentWithoutMkyc: boolean | null;
  attempts: number;
  error: string | null;
  sentAt: number | null;
  completedAt: number | null;
  updatedAt: number;
}

export interface PdCase {
  caseId: string;
  iccid: string;
  vehicleId: string | null;
  status: PdCaseStatus;
  sessionId: string | null;
  source: string | null;
  openedAt: number;
  updatedAt: number;
  note: string | null;
  vin: string | null;
  imei: string | null;
  customerName: string | null;
  customerMobile: string | null;
  cardState: string | null;
  msisdn1: string | null;
  rsuReadyAt: number | null;
  /**
   * Set while a person has paused the case: nothing goes to Sensorise, the
   * sweep skips it, both buttons are off. Status is unchanged, so the row
   * keeps its place in the queue. A case opened on Hold starts this way.
   */
  pausedAt: number | null;
  pauseNote: string | null;
  steps: PdCaseStep[];
  /**
   * Computed server-side, from the same rule triggerManualStep enforces: the
   * step is startable and every step before it has landed. Deriving it here
   * instead would let an enabled button produce a refusal.
   */
  canTriggerRsu: boolean;
  canTriggerBpc: boolean;
}

export interface PdCaseListData {
  pdCases: { cases: PdCase[]; total: number };
}

/**
 * A terminated SIM with no live case — the pool a revival is started from.
 *
 * Not a PdCase. Nothing has been sent to Sensorise for these yet, so there are
 * no steps, no srIds and no progress; the only question the row answers is
 * whether this SIM is worth opening a case for.
 */
export interface PdCandidate {
  iccid: string;
  cardState: string | null;
  msisdn1: string | null;
  /**
   * Epoch SECONDS, and null on most of them: Sensorise wipes the subscription
   * dates when it terminates a SIM, so "N/A" here is the normal case and not a
   * sync failure.
   */
  subscriptionEndDate: number | null;
  vehicleId: string | null;
  vin: string | null;
  imei: string | null;
  customerName: string | null;
  customerMobile: string | null;
  /**
   * Whether the vehicle's KYC is COMPLETE for a revival: owner name, mobile,
   * address and both proofs (type and number). The name is historical -- it
   * used to mean "a kyc_details row exists", which legacy rows with no proofs
   * satisfied, so case 70106221 (2026-09-28) opened on a green "Yes" and
   * blocked at once. When false the case still opens; step 1 waits BLOCKED
   * until KYC is added from the board, with nothing sent and no fee charged.
   */
  hasKycRecord: boolean;
}

export interface PdCandidateListData {
  pdCandidates: { candidates: PdCandidate[]; total: number };
}

export interface PdCaseSummaryData {
  pdCaseSummary: {
    OPEN: number;
    IN_PROGRESS: number;
    AWAITING_RSU: number;
    AWAITING_BPC: number;
    COMPLETED: number;
    FAILED: number;
    CANCELLED: number;
    CLOSED: number;
    PAUSED: number;
    CANDIDATES: number;
  };
}

export interface PdCaseResult {
  success: boolean;
  message: string;
  caseId: string | null;
}

const CASE_FIELDS = `
  caseId
  iccid
  status
  sessionId
  source
  openedAt
  updatedAt
  vin
  imei
  customerName
  customerMobile
  cardState
  msisdn1
  rsuReadyAt
  pausedAt
  pauseNote
  canTriggerRsu
  canTriggerBpc
  steps {
    stepId
    stepKey
    seq
    label
    status
    srId
    tokenNo
    remoteStatus
    remoteReason
    sentWithoutMkyc
    attempts
    error
    sentAt
    completedAt
    updatedAt
  }
`;

export const GET_PD_CASES = gql`
  query PdCases($offset: Int, $limit: Int, $search: String, $status: String, $fromDate: String, $toDate: String) {
    pdCases(offset: $offset, limit: $limit, search: $search, status: $status, fromDate: $fromDate, toDate: $toDate) {
      total
      cases { ${CASE_FIELDS} }
    }
  }
`;

export const GET_PD_CASE_SUMMARY = gql`
  query PdCaseSummary {
    pdCaseSummary {
      OPEN
      IN_PROGRESS
      AWAITING_RSU
      AWAITING_BPC
      COMPLETED
      FAILED
      CANCELLED
      CLOSED
      PAUSED
      CANDIDATES
    }
  }
`;

export const TRIGGER_PD_RSU = gql`
  mutation TriggerPdRsu($caseId: String!) {
    triggerPdRsu(caseId: $caseId) {
      success
      message
      caseId
    }
  }
`;

export const SKIP_PD_RSU = gql`
  mutation SkipPdRsu($caseId: String!) {
    skipPdRsu(caseId: $caseId) {
      success
      message
      caseId
    }
  }
`;

export const TRIGGER_PD_BPC = gql`
  mutation TriggerPdBpc($caseId: String!) {
    triggerPdBpc(caseId: $caseId) {
      success
      message
      caseId
    }
  }
`;

export const GET_PD_CANDIDATES = gql`
  query PdCandidates($offset: Int, $limit: Int, $search: String) {
    pdCandidates(offset: $offset, limit: $limit, search: $search) {
      total
      candidates {
        iccid
        cardState
        msisdn1
        subscriptionEndDate
        vehicleId
        vin
        imei
        customerName
        customerMobile
        hasKycRecord
      }
    }
  }
`;

/**
 * Opening a case is not free and not undoable: the backend inserts the case and
 * then fires step 1 immediately, which is a Commercial Activation carrying the
 * "OTC- RSU Fees (BSNL primary)" charge. Hence the confirmation on the board.
 *
 * A second case for the same ICCID is refused by a unique index, reported as
 * `success: false` with the server's own words rather than thrown, so a
 * double-click cannot pay the fee twice.
 */
export const OPEN_PD_CASE = gql`
  mutation OpenPdCase($iccid: String!, $hold: Boolean, $note: String) {
    openPdCase(iccid: $iccid, hold: $hold, note: $note) {
      success
      message
      caseId
    }
  }
`;

/**
 * Pause / resume. Pausing stops the automatic path -- no poll, no send, both
 * buttons off -- so operations can finish the revival by hand without the two
 * colliding on Sensorise's one-active-SR-per-ICCID rule. Nothing is undone;
 * resume picks up from wherever it stopped on the next sweep.
 */
export const PAUSE_PD_CASE = gql`
  mutation PausePdCase($caseId: String!, $note: String) {
    pausePdCase(caseId: $caseId, note: $note) {
      success
      message
      caseId
    }
  }
`;

/**
 * Close an unfinished case for good. Unlike pause there is no undo: the server
 * refuses to resume, cancel or advance a CLOSED case. Sends nothing to
 * Sensorise. The reason is required and appended to the case note.
 */
export const CLOSE_PD_CASE = gql`
  mutation ClosePdCase($caseId: String!, $reason: String!) {
    closePdCase(caseId: $caseId, reason: $reason) {
      success
      message
      caseId
    }
  }
`;

/**
 * Record the KYC a BLOCKED step 1 is waiting on, then send it.
 *
 * Sensorise refuses a revival's OTC RSU without the owner's ID and address
 * proof (LCM_234), and most terminated SIMs are pre-tool activations whose
 * kyc_details row carries a name and mobile but no proofs. The step parks
 * BLOCKED rather than spending the fee on a certain refusal, and this is the
 * way out. Before the board had it, the only way in was the GraphQL playground.
 *
 * Every field is optional: blanks leave the stored value alone, so an operator
 * who only has the missing POA sends just that. `success` is true only when the
 * step actually went out; otherwise the message names what is still missing,
 * and what was supplied is kept.
 */
export const SUPPLY_PD_CASE_KYC = gql`
  mutation SupplyPdCaseKyc($caseId: String!, $input: PdCaseKycInput!) {
    supplyPdCaseKyc(caseId: $caseId, input: $input) {
      success
      message
      caseId
    }
  }
`;

export interface PdCaseKycInput {
  proofOfIdentity?: string;
  poiIdNumber?: string;
  proofOfAddress?: string;
  poaIdNumber?: string;
  cusName?: string;
  cusContNo?: string;
  cusAdrs?: string;
}

export const RESUME_PD_CASE = gql`
  mutation ResumePdCase($caseId: String!) {
    resumePdCase(caseId: $caseId) {
      success
      message
      caseId
    }
  }
`;
